import { useState } from 'react'
import PropTypes from 'prop-types'
import requiredIf from 'react-required-if'
import {
  Nav,
  Navbar,
  NavDropdown,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap'
import { Icon } from '../icon'
import {
  faQuestionCircle,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons'
import styled from 'styled-components'

const HeaderStyled = styled.header`
  .header {
    font-size: 1.25rem;
    top: 10px;
    @media (min-width: 992px) {
      position: fixed;
      right: 0;
      left: 0;
      z-index: 1030;
    }
  }
`

const Options = ({ options, selectedOption, onOptionSelect, disabled }) => {
  const onSelect = (eventKey) =>
    onOptionSelect(options.find((it) => it.id === Number(eventKey)))

  return options.length > 1 ? (
    <Nav onSelect={onSelect} title='Select Option'>
      <NavDropdown
        disabled={disabled}
        id='select-option'
        title={selectedOption?.name}
      >
        {options.map((item) => (
          <NavDropdown.Item
            key={item.id}
            eventKey={item.id}
            active={item.id === selectedOption?.id}
          >
            {item.name}
          </NavDropdown.Item>
        ))}
      </NavDropdown>
    </Nav>
  ) : options.length === 1 ? (
    <Navbar.Text>{options[0].name}</Navbar.Text>
  ) : null
}

const Links = ({ links, disabled }) => (
  <Nav id='nav-links'>
    {links.map((link) => (
      <Nav.Link
        key={link.url}
        href={link.url}
        disabled={disabled}
        {...link.props}
      >
        {link.name}
      </Nav.Link>
    ))}
  </Nav>
)

const UserMenu = ({
  userName,
  userOptions,
  onUserOptionSelect,
  loggedIn,
  onLogin,
  disabled
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState()

  const onSelect = (eventKey) =>
    onUserOptionSelect(userOptions.find((it) => it.id === Number(eventKey)))

  return (
    <Nav>
      {loggedIn ? (
        <OverlayTrigger
          placement='bottom'
          overlay={
            isDropdownOpen ? (
              <span />
            ) : (
              <Tooltip id='user-tooltip'>Account</Tooltip>
            )
          }
        >
          <NavDropdown
            disabled={disabled}
            title={
              <span>
                <Icon icon={faUserCircle} size='lg' />
                <span>{userName || null}</span>
              </span>
            }
            id='nav-dropdown-user'
            onToggle={(show) => setIsDropdownOpen(show)}
            onSelect={onSelect}
          >
            {userOptions.map((item) => (
              <NavDropdown.Item key={item.id} eventKey={item.id} active={false}>
                {item.name}
              </NavDropdown.Item>
            ))}
          </NavDropdown>
        </OverlayTrigger>
      ) : (
        <Nav.Link role='button' onClick={() => onLogin()}>
          <Icon icon={faUserCircle} size='lg' />
          Log In
        </Nav.Link>
      )}
    </Nav>
  )
}

const HelpMenu = ({ helpOptions, onHelpOptionSelect, disabled }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState()

  const onSelect = (eventKey) =>
    onHelpOptionSelect(helpOptions.find((it) => it.id === Number(eventKey)))

  return (
    <Nav>
      <OverlayTrigger
        placement='bottom'
        overlay={
          isDropdownOpen ? <span /> : <Tooltip id='help-tooltip'>Help</Tooltip>
        }
      >
        <NavDropdown
          disabled={disabled}
          title={
            <span>
              <Icon icon={faQuestionCircle} size='lg' />
              <span className='sr-only'>Help Menu</span>
            </span>
          }
          id='nav-dropdown-help'
          onToggle={(show) => setIsDropdownOpen(show)}
          onSelect={onSelect}
        >
          {helpOptions.map((item) => (
            <NavDropdown.Item key={item.id} eventKey={item.id} active={false}>
              {item.name}
            </NavDropdown.Item>
          ))}
        </NavDropdown>
      </OverlayTrigger>
    </Nav>
  )
}

/** the header for the site */
const Header = ({
  options,
  selectedOption,
  onOptionSelect,
  links,
  disabled,
  loggedIn,
  onLogin,
  userName,
  userOptions,
  onUserOptionSelect,
  helpOptions,
  onHelpOptionSelect,
  ...other
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <HeaderStyled>
      <Navbar {...other} defaultExpanded={false} onToggle={setExpanded}>
        <Navbar.Toggle aria-expanded={expanded} />
        <Navbar.Collapse id='basic-navbar-nav'>
          {options?.length ? (
            <Options
              options={options}
              selectedOption={selectedOption}
              onOptionSelect={onOptionSelect}
              disabled={disabled}
            />
          ) : null}
          {links?.length ? <Links links={links} disabled={disabled} /> : null}
          <Nav className='ml-auto'>
            {helpOptions?.length ? (
              <HelpMenu
                helpOptions={helpOptions}
                onHelpOptionSelect={onHelpOptionSelect}
                disabled={disabled}
              />
            ) : null}
            <UserMenu
              userName={userName}
              userOptions={userOptions}
              onUserOptionSelect={onUserOptionSelect}
              loggedIn={loggedIn}
              onLogin={onLogin}
              disabled={disabled}
            />
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    </HeaderStyled>
  )
}

Header.propTypes = {
  /** items for the left hand dropdown menu */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  /** The selected item for the left hand dropdown menu */
  selectedOption: requiredIf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    }),
    (props) => props.options && props.options.length > 1
  ),
  /** handler for selection of an item */
  onOptionSelect: requiredIf(
    PropTypes.func,
    (props) => props.options && props.options.length > 1
  ),
  /** the links running horizontal across the header */
  links: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      props: PropTypes.object
    })
  ),
  /** toggle if components within header are interactive */
  disabled: PropTypes.bool,
  /** boolean value indicating authentication status */
  loggedIn: PropTypes.bool,
  /** handler for selection of login button */
  onLogin: requiredIf(PropTypes.func, (props) => !props.loggedIn),
  /** string displayed as user menu */
  userName: PropTypes.string,
  /** items for the user menu */
  userOptions: requiredIf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
      })
    ),
    (props) => props.loggedIn
  ),
  /** handler for selection of an item from the user menu */
  onUserOptionSelect: requiredIf(PropTypes.func, (props) => props.loggedIn),
  /** items for the help menu */
  helpOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  /** handler for selection of an item from the help menu */
  onHelpOptionSelect: requiredIf(PropTypes.func, (props) => props.helpOptions)
}

export { Header }
